import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Play, Sparkles, RefreshCw, CheckCircle2, AlertCircle, Loader2, X, Eye, Copy, Download, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useLanguage } from '@/contexts/LanguageContext';
import { useWorkflows } from '@/hooks/useWorkflows';
import { UsageWarningBanner } from '@/components/UsageWarningBanner';
import { WorkflowFilters } from '@/components/workflows/WorkflowFilters';
import { RunHistoryTable } from '@/components/workflows/RunHistoryTable';
import { WorkflowDetailModal } from '@/components/workflows/WorkflowDetailModal';
import { FriendlyError } from '@/components/ErrorBoundary';
import { cn } from '@/lib/utils';

/**
 * WorkflowsPage (`/v2/workflows`) — Phase 3 W11-12 enhanced.
 *   • Category filter pills + search input
 *   • Sortable, status-filterable run history
 *   • Workflow detail modal (overview / edit / improve)
 *   • Edit before run, duplicate, improve via LLM
 *   • Download active run output as .docx / .pdf
 */
export default function WorkflowsPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isArabic = language === 'ar';

  const {
    templates, runs, error,
    listTemplates, listRuns, generate, runWorkflow, runWorkflowStream, getRun, resumeRun,
    saveWorkflow, duplicateWorkflow, improveWorkflow, renderDocument,
  } = useWorkflows();

  const [description, setDescription] = useState('');
  const [generated, setGenerated] = useState(null);
  const [activeRun, setActiveRun] = useState(null);
  const [runInput, setRunInput] = useState({ document_text: '' });
  const [showApproval, setShowApproval] = useState(false);
  const [approvalNote, setApprovalNote] = useState('');
  const [busy, setBusy] = useState(false);

  // Phase 4 W15-16 — live progress panel for streaming runs.
  // Each entry: { node_id, node_name, status, elapsed_s, message }
  const [liveNodes, setLiveNodes] = useState([]);
  const [liveStatus, setLiveStatus] = useState(null);
  const liveCancelRef = useRef(null);

  // New W11-12 state
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('started_at');
  const [sortDir, setSortDir] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailWorkflow, setDetailWorkflow] = useState(null);

  // Auto-open approval modal when a run pauses
  useEffect(() => {
    if (activeRun?.status === 'paused' && activeRun?.pending_approval) setShowApproval(true);
  }, [activeRun]);

  const t = (en, ar) => (isArabic ? ar : en);

  const allWorkflows = useMemo(() => {
    if (!generated) return templates;
    if (templates.some((tt) => tt.id === generated.id)) return templates;
    return [generated, ...templates];
  }, [templates, generated]);

  const filteredWorkflows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allWorkflows.filter((w) => {
      if (category !== 'all' && (w.category || 'general') !== category) return false;
      if (!q) return true;
      const hay = `${w.name || ''} ${w.name_ar || ''} ${w.description || ''} ${w.description_ar || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [allWorkflows, category, search]);

  const filteredRuns = useMemo(() => {
    let out = (runs || []).slice();
    if (statusFilter !== 'all') out = out.filter((r) => r.status === statusFilter);
    out.sort((a, b) => {
      const va = a[sortKey] ?? '';
      const vb = b[sortKey] ?? '';
      if (va === vb) return 0;
      return (va > vb ? 1 : -1) * (sortDir === 'asc' ? 1 : -1);
    });
    return out;
  }, [runs, sortKey, sortDir, statusFilter]);

  const onSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('desc'); }
  };

  // ── Handlers ──────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!description.trim()) return;
    setBusy(true);
    try {
      const wf = await generate(description, language);
      setGenerated(wf);
      toast.success(t('Workflow generated', 'تم توليد سير العمل'));
    } catch (e) {
      toast.error(t('Generation failed', 'فشل التوليد'));
    } finally { setBusy(false); }
  };

  const handleRun = async (workflowId) => {
    // Cancel any in-flight stream from a previous run
    try { liveCancelRef.current?.(); } catch { /* noop */ }
    liveCancelRef.current = null;

    setBusy(true);
    setLiveNodes([]);
    setLiveStatus('starting');
    setActiveRun(null);
    try {
      const initial = {};
      if (runInput.document_text?.trim()) initial.document_text = runInput.document_text;

      const cancel = await runWorkflowStream(workflowId, initial, language, (evt) => {
        if (!evt || !evt.event) return;
        switch (evt.event) {
          case 'stream_start':
            setLiveStatus('running');
            break;

          case 'node_started':
            setLiveNodes((prev) => {
              const next = prev.filter((n) => n.node_id !== evt.node_id);
              return [...next, {
                node_id: evt.node_id,
                node_name: evt.node_name || evt.node_id,
                node_type: evt.node_type || '',
                status: 'running',
                elapsed_s: 0,
                message: '',
              }];
            });
            break;

          case 'node_progress':
            setLiveNodes((prev) => prev.map((n) =>
              n.node_id === evt.node_id ? { ...n, message: evt.message || n.message } : n,
            ));
            break;

          case 'node_completed':
            setLiveNodes((prev) => prev.map((n) =>
              n.node_id === evt.node_id ? {
                ...n,
                status: 'completed',
                elapsed_s: evt.elapsed_s ?? n.elapsed_s,
              } : n,
            ));
            break;

          case 'node_failed':
            setLiveNodes((prev) => prev.map((n) =>
              n.node_id === evt.node_id ? {
                ...n,
                status: 'failed',
                elapsed_s: evt.elapsed_s ?? n.elapsed_s,
                message: evt.error || n.message,
              } : n,
            ));
            break;

          case 'approval_required':
            setLiveStatus('paused');
            // Hydrate the approval modal immediately from the SSE event
            // — no need to wait for a polling cycle.
            getRun(evt.run_id).then((run) => {
              if (run) {
                setActiveRun(run);
                setShowApproval(true);
              }
            }).catch(() => {});
            break;

          case 'workflow_complete':
            setLiveStatus('completed');
            getRun(evt.run_id).then((run) => {
              if (run) setActiveRun(run);
            }).catch(() => {});
            listRuns().catch(() => {});
            break;

          case 'workflow_failed':
            setLiveStatus('failed');
            toast.error(t('Run failed', 'فشل التشغيل') + `: ${evt.error || ''}`);
            listRuns().catch(() => {});
            break;

          case 'done':
            setBusy(false);
            break;

          case 'error':
            setLiveStatus('failed');
            toast.error(t('Stream error', 'خطأ في البث') + `: ${evt.error || ''}`);
            setBusy(false);
            break;

          default:
            break;
        }
      });
      liveCancelRef.current = cancel;
    } catch (e) {
      toast.error(t('Run failed', 'فشل التشغيل'));
      setLiveStatus('failed');
      setBusy(false);
    }
  };

  const handleResume = async () => {
    if (!activeRun) return;
    setBusy(true);
    try {
      const run = await resumeRun(activeRun.id, {
        approved_by: 'user',
        note: approvalNote,
        approved_at: new Date().toISOString(),
      });
      setActiveRun(run);
      setShowApproval(false);
      setApprovalNote('');
      listRuns().catch(() => {});
    } catch (e) {
      toast.error(t('Resume failed', 'فشل الاستئناف'));
    } finally { setBusy(false); }
  };

  const handleRefreshActive = async () => {
    if (!activeRun) return;
    try {
      const fresh = await getRun(activeRun.id);
      setActiveRun(fresh);
    } catch (e) { /* ignore */ }
  };

  const handleDuplicate = async (workflowId) => {
    setBusy(true);
    try {
      const wf = await duplicateWorkflow(workflowId);
      setGenerated(wf);
      setDetailWorkflow(wf);
      toast.success(t('Duplicated', 'تم التكرار'));
    } catch (e) {
      toast.error(t('Duplicate failed', 'فشل التكرار'));
    } finally { setBusy(false); }
  };

  const handleSaveEdited = async (draft) => {
    setBusy(true);
    try {
      const wf = await saveWorkflow(draft);
      setGenerated(wf);
      setDetailWorkflow(wf);
      toast.success(t('Workflow saved', 'تم حفظ سير العمل'));
    } catch (e) {
      toast.error(t('Save failed', 'فشل الحفظ'));
    } finally { setBusy(false); }
  };

  const handleImprove = async (workflowId, feedback) => {
    setBusy(true);
    try {
      const result = await improveWorkflow(workflowId, feedback);
      setDetailWorkflow(result.workflow);
      setGenerated(result.workflow);
      toast.success(result.improvement_notes
        ? t('Improvement ready — review changes', 'الاقتراح جاهز — راجع التغييرات')
        : t('Improvement ready', 'الاقتراح جاهز'));
    } catch (e) {
      toast.error(t('Improve failed', 'فشل التحسين'));
    } finally { setBusy(false); }
  };

  const handleDownload = async (format) => {
    if (!activeRun?.node_outputs?.output) return;
    const out = activeRun.node_outputs.output;
    const content = (
      out.draft_text || out.review_text || out.answer ||
      Object.entries(out).map(([k, v]) => `## ${k}\n\n${typeof v === 'string' ? v : JSON.stringify(v, null, 2)}`).join('\n\n')
    );
    try {
      await renderDocument({
        content,
        format,
        title: activeRun.workflow_name || 'workflow_output',
        language,
      });
    } catch (e) {
      toast.error(t('Download failed', 'فشل التنزيل'));
    }
  };

  return (
    <div className="min-h-full w-full" data-testid="workflows-page">
      <div className="max-w-6xl mx-auto px-6 py-8">
        <UsageWarningBanner />

        <div className="flex items-start justify-between gap-4 mb-1 mt-6">
          <div className="flex-1 min-w-0">
            <h1 className="text-3xl sm:text-4xl font-semibold hv-text font-arabic">
              {t('Legal Workflows', 'مسارات العمل القانونية')}
            </h1>
            <p className="hv-text-muted text-sm max-w-2xl mt-1 mb-8 font-arabic">
              {t(
                'Design a multi-step legal workflow, or choose from ready-made templates.',
                'صمم مسار عمل قانوني متعدد الخطوات، أو اختر من النماذج الجاهزة.'
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => document.querySelector('[data-testid="workflows-generate-input"]')?.focus()}
            className="shrink-0 px-4 py-2 bg-black text-white text-[13px] font-arabic transition hover:bg-black/85"
            style={{ borderRadius: 'var(--radius-pill)' }}
            data-testid="workflows-create-cta"
          >
            {isArabic ? '+ إنشاء مسار عمل' : '+ Create workflow'}
          </button>
        </div>

        {error && (
          <div className="mb-4">
            <FriendlyError
              isArabic={isArabic}
              title={isArabic ? 'تعذر تحميل سير العمل' : 'Could not load Workflows'}
              description={isArabic
                ? 'حدث خلل مؤقت أثناء جلب القوالب أو سجل التشغيل. حاول مرة أخرى.'
                : 'A temporary issue occurred while loading templates or run history. Please retry.'}
              onRetry={() => { listTemplates().catch(() => {}); listRuns().catch(() => {}); }}
            />
          </div>
        )}

        {/* NL Generator */}
        <section className="hv-border rounded-lg p-5 mb-10" data-testid="workflows-generate-card">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 hv-tag-policy" />
            <h2 className="text-base font-semibold hv-text font-arabic">
              {t('Generate from natural language', 'توليد من وصف طبيعي')}
            </h2>
          </div>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t(
              'e.g., "Audit a privacy policy against PDPL, ask a lawyer to approve, then output the final report."',
              'مثال: راجع سياسة الخصوصية مقابل نظام حماية البيانات…'
            )}
            rows={3}
            className="w-full hv-border hv-surface-alt rounded p-3 text-sm hv-text resize-none focus:outline-none focus:hv-border-strong font-arabic"
            data-testid="workflows-generate-input"
          />
          <div className="flex justify-end mt-3">
            <button
              onClick={handleGenerate}
              disabled={busy || !description.trim()}
              className="hv-btn hv-btn-primary inline-flex items-center gap-2 font-arabic"
              data-testid="workflows-generate-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {t('Generate workflow', 'توليد مسار العمل')}
            </button>
          </div>
          {generated && (
            <div className="mt-4 hv-border-t pt-4 text-xs hv-text-muted flex items-center justify-between gap-4" data-testid="workflows-generated-summary">
              <div>
                <div className="hv-text font-semibold mb-1">{isArabic ? generated.name_ar : generated.name}</div>
                <div>
                  {generated.nodes.length} {t('nodes', 'عقدة')} · {t('start', 'البداية')}: <code className="hv-tag-policy">{generated.start_node_id}</code>
                </div>
              </div>
              <button
                className="hv-btn text-xs inline-flex items-center gap-1"
                onClick={() => setDetailWorkflow(generated)}
                data-testid="workflows-generated-open"
              >
                <Eye className="w-3 h-3" />
                {t('Open', 'فتح')}
              </button>
            </div>
          )}
        </section>

        {/* Filters */}
        <section className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-base font-semibold hv-text font-arabic">{t('Templates', 'النماذج الجاهزة')}</h2>
            <span className="text-xs hv-text-subtle">{filteredWorkflows.length} / {allWorkflows.length} {t('shown', 'معروض')}</span>
          </div>
          <WorkflowFilters
            category={category}
            onCategoryChange={setCategory}
            search={search}
            onSearchChange={setSearch}
            isArabic={isArabic}
          />
          {filteredWorkflows.length === 0 ? (
            <div className="hv-text-subtle text-sm" data-testid="workflows-empty">
              {t('No workflows match the current filters.', 'لا توجد قوالب مطابقة للفلاتر.')}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-2" data-testid="workflows-grid">
              {filteredWorkflows.map((wf) => (
                <WorkflowCard
                  key={wf.id}
                  workflow={wf}
                  isArabic={isArabic}
                  disabled={busy}
                  onRun={() => handleRun(wf.id)}
                  onOpen={() => setDetailWorkflow(wf)}
                  onDuplicate={() => handleDuplicate(wf.id)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Optional input */}
        <section className="hv-border rounded-lg p-5 mt-10 mb-12">
          <h2 className="text-sm font-semibold hv-text mb-2">{t('Optional input', 'مدخل اختياري')}</h2>
          <p className="text-xs hv-text-muted mb-3">
            {t(
              'If a template needs a contract or policy text, paste it here before clicking Run.',
              'إذا احتاج القالب إلى نص عقد أو سياسة، الصقه هنا قبل تشغيل سير العمل.'
            )}
          </p>
          <textarea
            value={runInput.document_text}
            onChange={(e) => setRunInput({ ...runInput, document_text: e.target.value })}
            placeholder={t('Document text…', 'نص المستند…')}
            rows={4}
            className="w-full hv-border hv-surface-alt rounded p-3 text-sm hv-text resize-none focus:outline-none focus:hv-border-strong"
            data-testid="workflows-input-document"
          />
        </section>

        {/* Live progress (Phase 4 W15-16 SSE streaming run) */}
        {(liveStatus || liveNodes.length > 0) && (
          <LiveProgressPanel
            isArabic={isArabic}
            status={liveStatus}
            nodes={liveNodes}
            onClose={() => { setLiveStatus(null); setLiveNodes([]); }}
          />
        )}

        {/* Active run */}
        {activeRun && (
          <RunPanel
            run={activeRun}
            isArabic={isArabic}
            onRefresh={handleRefreshActive}
            onApprove={() => setShowApproval(true)}
            onClose={() => setActiveRun(null)}
            onDownload={handleDownload}
          />
        )}

        {/* Run history */}
        <RunHistoryTable
          runs={filteredRuns}
          isArabic={isArabic}
          sortKey={sortKey}
          sortDir={sortDir}
          statusFilter={statusFilter}
          onSort={onSort}
          onStatusFilterChange={setStatusFilter}
          onOpen={async (r) => {
            const fresh = await getRun(r.id);
            setActiveRun(fresh);
          }}
        />
      </div>

      {/* Approval modal */}
      {showApproval && activeRun?.pending_approval && (
        <ApprovalModal
          isArabic={isArabic}
          run={activeRun}
          note={approvalNote}
          onNoteChange={setApprovalNote}
          onClose={() => setShowApproval(false)}
          onApprove={handleResume}
          busy={busy}
        />
      )}

      {/* Workflow detail / edit / improve modal */}
      {detailWorkflow && (
        <WorkflowDetailModal
          workflow={detailWorkflow}
          isArabic={isArabic}
          onClose={() => setDetailWorkflow(null)}
          onSave={handleSaveEdited}
          onImprove={handleImprove}
          onDuplicate={handleDuplicate}
          busy={busy}
        />
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────
function WorkflowCard({ workflow, isArabic, onRun, onOpen, onDuplicate, disabled }) {
  const name = isArabic ? workflow.name_ar : workflow.name;
  const desc = isArabic ? workflow.description_ar : workflow.description;
  const nodeTypes = workflow.nodes.map((n) => n.type);
  const hasHuman = nodeTypes.includes('human');
  const hasDecision = nodeTypes.includes('decision');

  return (
    <div
      className="hv-border rounded-lg p-4 flex flex-col gap-3 hover:hv-border-strong transition-colors"
      data-testid={`workflow-card-${workflow.id}`}
    >
      <div>
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm font-semibold hv-text">{name}</h3>
          <span className="text-[10px] uppercase tracking-wider hv-text-subtle">{workflow.category}</span>
        </div>
        <p className="hv-text-muted text-xs mt-1 line-clamp-3 min-h-[2.5rem]">{desc}</p>
      </div>

      <div className="flex flex-wrap gap-1">
        <span className="hv-bg-policy text-[10px] px-2 py-0.5 rounded">
          {workflow.nodes.length} {isArabic ? 'عقدة' : 'nodes'}
        </span>
        {hasHuman && (
          <span className="hv-bg-compliant text-[10px] px-2 py-0.5 rounded">
            {isArabic ? 'موافقة بشرية' : 'human-in-loop'}
          </span>
        )}
        {hasDecision && (
          <span className="hv-bg-risk text-[10px] px-2 py-0.5 rounded">
            {isArabic ? 'تفرّع شرطي' : 'decision'}
          </span>
        )}
      </div>

      <div className="flex justify-end gap-1.5 mt-auto">
        <button
          onClick={onOpen}
          disabled={disabled}
          className="hv-btn text-xs inline-flex items-center gap-1"
          data-testid={`workflow-open-${workflow.id}`}
          title={isArabic ? 'فتح' : 'Open'}
        >
          <Eye className="w-3 h-3" />
        </button>
        <button
          onClick={onDuplicate}
          disabled={disabled}
          className="hv-btn text-xs inline-flex items-center gap-1"
          data-testid={`workflow-duplicate-${workflow.id}`}
          title={isArabic ? 'تكرار' : 'Duplicate'}
        >
          <Copy className="w-3 h-3" />
        </button>
        <button
          onClick={onRun}
          disabled={disabled}
          className="hv-btn hv-btn-primary text-xs inline-flex items-center gap-1.5"
          data-testid={`workflow-run-${workflow.id}`}
        >
          <Play className="w-3 h-3" />
          {isArabic ? 'تشغيل' : 'Run'}
        </button>
      </div>
    </div>
  );
}

function LiveProgressPanel({ isArabic, status, nodes, onClose }) {
  const t = (en, ar) => (isArabic ? ar : en);
  const statusLabel = {
    starting:  t('Starting…',  'يبدأ…'),
    running:   t('Running',    'قيد التشغيل'),
    paused:    t('Paused — awaiting approval', 'متوقف — بانتظار الاعتماد'),
    completed: t('Completed',  'اكتمل'),
    failed:    t('Failed',     'فشل'),
  }[status] || status || '';

  const dotColor = {
    starting:  '#888',
    running:   '#1f6feb',
    paused:    '#a16207',
    completed: '#1F5A2D',
    failed:    '#911',
  }[status] || '#888';

  const nodeIcon = (s) => {
    if (s === 'running') return <Loader2 className="w-3.5 h-3.5 animate-spin" />;
    if (s === 'completed') return <CheckCircle2 className="w-3.5 h-3.5" style={{ color: '#1F5A2D' }} />;
    if (s === 'failed') return <AlertCircle className="w-3.5 h-3.5" style={{ color: '#911' }} />;
    return <Clock className="w-3.5 h-3.5" style={{ color: '#888' }} />;
  };

  return (
    <section
      className="hv-border-strong hv-border rounded-lg p-5 mb-6"
      data-testid="workflows-live-progress"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ background: dotColor }}
            data-testid="workflows-live-status-dot"
          />
          <h2 className="text-sm font-semibold hv-text">
            {t('Live progress', 'تقدم مباشر')}
          </h2>
          <span
            className="text-xs hv-text-muted"
            data-testid="workflows-live-status-label"
          >
            {statusLabel}
          </span>
        </div>
        <button
          onClick={onClose}
          className="hv-btn text-xs flex items-center gap-1"
          data-testid="workflows-live-close"
        >
          <X className="w-3.5 h-3.5" /> {t('Hide', 'إخفاء')}
        </button>
      </div>

      {nodes.length === 0 ? (
        <div className="hv-text-subtle text-xs" data-testid="workflows-live-empty">
          {t('Waiting for the first node…', 'بانتظار أول خطوة…')}
        </div>
      ) : (
        <ul className="space-y-2" data-testid="workflows-live-nodes">
          {nodes.map((n) => (
            <li
              key={n.node_id}
              className="flex items-start gap-2 text-xs hv-text"
              data-testid={`workflows-live-node-${n.node_id}`}
            >
              <span className="pt-0.5">{nodeIcon(n.status)}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium truncate">
                    {n.node_name}
                    {n.node_type ? <span className="hv-text-subtle"> · {n.node_type}</span> : null}
                  </span>
                  <span className="hv-text-subtle tabular-nums">
                    {n.elapsed_s ? `${n.elapsed_s.toFixed(1)}s` : ''}
                  </span>
                </div>
                {n.message ? (
                  <div className="hv-text-subtle text-[11px] mt-0.5 truncate">
                    {n.message}
                  </div>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}


function RunPanel({ run, isArabic, onRefresh, onApprove, onClose, onDownload }) {
  const t = (en, ar) => (isArabic ? ar : en);
  const hasFinalOutput = run.status === 'completed' && run.node_outputs?.output;
  return (
    <section className="hv-border-strong hv-border rounded-lg p-5 mb-10" data-testid={`workflows-run-${run.id}`}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold hv-text">{t('Active run', 'التشغيل الحالي')}</h2>
          <RunStatusBadge status={run.status} />
          <span className="text-xs hv-text-subtle font-mono">{run.id.slice(-12)}</span>
        </div>
        <div className="flex items-center gap-2">
          {hasFinalOutput && (
            <>
              <button
                onClick={() => onDownload('docx')}
                className="hv-btn text-xs inline-flex items-center gap-1"
                data-testid="workflows-run-download-docx"
              >
                <Download className="w-3 h-3" /> .docx
              </button>
              <button
                onClick={() => onDownload('pdf')}
                className="hv-btn text-xs inline-flex items-center gap-1"
                data-testid="workflows-run-download-pdf"
              >
                <Download className="w-3 h-3" /> .pdf
              </button>
            </>
          )}
          <button onClick={onRefresh} className="hv-btn text-xs inline-flex items-center gap-1" data-testid="workflows-run-refresh">
            <RefreshCw className="w-3 h-3" /> {t('Refresh', 'تحديث')}
          </button>
          {run.status === 'paused' && (
            <button onClick={onApprove} className="hv-btn hv-btn-primary text-xs" data-testid="workflows-run-approve">
              {t('Approve & resume', 'اعتماد ومتابعة')}
            </button>
          )}
          <button onClick={onClose} className="hv-text-muted hover:hv-text" data-testid="workflows-run-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {run.error && <div className="text-xs hv-tag-risk mb-3">{run.error}</div>}

      <div className="space-y-1.5">
        {Object.entries(run.node_states || {}).map(([nodeId, state]) => (
          <div key={nodeId} className="flex items-center justify-between text-xs hv-border-b py-1.5">
            <code className="hv-text">{nodeId}</code>
            <NodeStateBadge state={state} />
          </div>
        ))}
      </div>

      {hasFinalOutput && (
        <details className="mt-4" data-testid="workflows-run-final-output">
          <summary className="text-xs hv-text-muted cursor-pointer">{t('Final output', 'المخرجات النهائية')}</summary>
          <pre className="mt-2 hv-surface-alt hv-border rounded p-3 text-[11px] overflow-x-auto whitespace-pre-wrap">
            {JSON.stringify(run.node_outputs.output, null, 2)}
          </pre>
        </details>
      )}
    </section>
  );
}

function ApprovalModal({ isArabic, run, note, onNoteChange, onClose, onApprove, busy }) {
  const t = (en, ar) => (isArabic ? ar : en);
  const pa = run.pending_approval || {};
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={onClose}
      data-testid="workflows-approval-overlay"
    >
      <div
        className="hv-surface hv-border-strong hv-border rounded-lg p-6 w-full max-w-lg mx-4"
        onClick={(e) => e.stopPropagation()}
        data-testid="workflows-approval-modal"
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-semibold hv-text">{t('Human approval required', 'مطلوب اعتماد بشري')}</h3>
          <button onClick={onClose} className="hv-text-muted hover:hv-text">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="hv-text-muted text-sm mb-3">{pa.prompt}</p>

        {pa.payload && Object.keys(pa.payload).length > 0 && (
          <details className="mb-3">
            <summary className="text-xs hv-text-subtle cursor-pointer">{t('Review payload', 'مراجعة المحتوى')}</summary>
            <pre className="mt-2 hv-surface-alt hv-border rounded p-3 text-[11px] overflow-x-auto max-h-60 whitespace-pre-wrap">
              {JSON.stringify(pa.payload, null, 2)}
            </pre>
          </details>
        )}

        <textarea
          rows={3}
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder={t('Add an approval note (optional)…', 'أضف ملاحظة اعتماد (اختياري)…')}
          className="w-full hv-border hv-surface-alt rounded p-2 text-sm resize-none focus:outline-none focus:hv-border-strong"
          data-testid="workflows-approval-note"
        />

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="hv-btn text-sm" data-testid="workflows-approval-cancel">
            {t('Cancel', 'إلغاء')}
          </button>
          <button
            onClick={onApprove}
            disabled={busy}
            className="hv-btn hv-btn-primary text-sm inline-flex items-center gap-2"
            data-testid="workflows-approval-confirm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {t('Approve & continue', 'اعتماد ومتابعة')}
          </button>
        </div>
      </div>
    </div>
  );
}

function RunStatusBadge({ status }) {
  const map = {
    pending: 'hv-bg-policy', running: 'hv-bg-policy',
    paused: 'hv-bg-risk', completed: 'hv-bg-compliant', failed: 'hv-bg-risk',
  };
  const labelAr = { pending: 'معلق', running: 'جاري', paused: 'متوقف', completed: 'مكتمل', failed: 'فشل' };
  const cls = map[status] || 'hv-bg-policy';
  return (
    <span
      className={cn('text-[10px] px-2 py-0.5 rounded uppercase tracking-wider font-arabic', cls)}
      data-testid={`run-status-${status}`}
      data-status-en={status}
    >
      {labelAr[status] || status}
    </span>
  );
}

function NodeStateBadge({ state }) {
  if (state === 'completed') return <span className="hv-tag-compliant inline-flex items-center gap-1 font-arabic"><CheckCircle2 className="w-3 h-3" />مكتمل</span>;
  if (state === 'failed') return <span className="hv-tag-risk inline-flex items-center gap-1 font-arabic"><AlertCircle className="w-3 h-3" />فشل</span>;
  if (state === 'running') return <span className="hv-tag-policy inline-flex items-center gap-1 font-arabic"><Loader2 className="w-3 h-3 animate-spin" />جاري</span>;
  if (state === 'pending') return <span className="hv-text-subtle font-arabic">معلق</span>;
  if (state === 'paused') return <span className="hv-tag-risk inline-flex items-center gap-1 font-arabic">متوقف</span>;
  return <span className="hv-text-subtle">{state}</span>;
}
